// Build tab → Build: what to know before pressing "Build AAB". An AAB goes to
// Play, so it should carry exactly the code QA checked. QA checks a repo, not
// a bundle: the newest verdict on any APK from this app's base repo and branch
// counts here, and new commits undo a pass (a failure stays until a later pass).
import { shortBuildId } from '@/lib/build-id';
import { formatDateTime } from '@/lib/datetime';
import type { AabCheck, AabQaState } from '../../api/app-builds';

const short = (sha: string) => sha.slice(0, 7);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function fmtUnix(s: number | null): string {
  return s ? new Date(s * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

/** "an APK from this repo, on <bundle>" / "the last APK of this app". */
function whichApk(qa: NonNullable<AabCheck['qa']>): string {
  if (!qa.this_app) return `an APK from this repo, on ${qa.bundle_id ?? qa.app_id}`;
  return qa.is_last_apk ? 'the last APK of this app' : 'an earlier APK of this app';
}

/** The header lines of the panel: why the AAB button is the colour it is. */
export function aabReasons(c: AabCheck | undefined): string[] {
  if (!c) return [];
  const { qa, repo } = c;
  const ahead = repo?.ahead_by;
  const out: string[] = [];
  if (c.state === 'safe' && qa) {
    out.push(`QA checked ${whichApk(qa)}`, 'No new commits since that APK');
  } else if (c.state === 'failed' && qa) {
    out.push(`QA failed ${whichApk(qa)}`);
    if (ahead === 0) out.push('No new commits since that APK');
    else if (ahead) out.push(`The repo has ${plural(ahead, 'new commit')} since that APK. Build an APK and have QA check the fix`);
  } else {
    out.push(qa ? 'No APK from the current code of this repo has been checked by QA' : 'No APK from this repo has been checked by QA');
    if (repo?.since === 'last_apk' && ahead) out.push(`The repo has ${plural(ahead, 'new commit')} since the last APK`);
  }
  return out;
}

const TONE: Record<AabQaState, { head: string; title: string }> = {
  safe: { head: 'bg-success-soft/40 text-success', title: 'Safe to build an AAB' },
  unchecked: { head: 'bg-warning-soft/40 text-warning', title: 'Before building an AAB' },
  failed: { head: 'bg-danger-soft/40 text-danger', title: "Don't build an AAB" },
};

/** Build AAB button colour; the button stays clickable whatever it says. */
export const AAB_BUTTON: Record<AabQaState, string> = {
  safe: 'bg-green-500 text-green-950',
  unchecked: 'bg-yellow-500 text-yellow-950',
  failed: 'bg-red-500 text-white',
};

export function AabStateIcon({ state }: { state: AabQaState }) {
  return (
    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 shrink-0" aria-hidden="true">
      {state === 'safe' ? (
        <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : state === 'failed' ? (
        <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      ) : (
        <>
          <path d="M8 2L1.5 13.5h13z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M8 6.5v3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="8" cy="11.6" r=".9" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'err'; children: React.ReactNode }) {
  const cls = tone === 'ok' ? 'bg-success-soft text-success' : tone === 'err' ? 'bg-danger-soft text-danger' : 'bg-warning-soft text-warning';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{children}</span>;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-3 py-2 flex flex-wrap items-start gap-x-4 gap-y-1">
      <span className="w-20 shrink-0 text-xs uppercase tracking-wide text-content-subtle pt-0.5">{label}</span>
      <div className="min-w-0 flex-1 space-y-1">{children}</div>
    </div>
  );
}

const SINCE_LABEL = { checked: 'since the checked APK', failed: 'since the failed APK', last_apk: 'since the last APK' } as const;

export function AabCheckPanel({ check, loading }: { check: AabCheck | undefined; loading: boolean }) {
  if (loading) return <div className="text-sm text-content-subtle">Checking QA and the repo…</div>;
  if (!check) return null;
  const { apk, qa, repo, state } = check;
  const reasons = aabReasons(check);
  const shownQa = qa && !qa.stale ? qa : null;

  return (
    <div className="rounded-md border border-border divide-y divide-border text-sm">
      <div className={`px-3 py-2 ${TONE[state].head}`}>
        <div className="font-medium">{TONE[state].title}</div>
        <ul className="mt-0.5 text-xs list-disc pl-4">
          {reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </div>

      <Row label="Last APK">
        {apk ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-content">v{apk.version_name} ({apk.version_code})</span>
            <span className="font-mono text-xs text-content-subtle" title={`Build ${apk.id}`}>#{shortBuildId(apk.id)}</span>
            <span className="text-xs text-content-subtle">{fmtUnix(apk.created_at)}</span>
          </div>
        ) : <span className="text-content-subtle">None yet.</span>}
      </Row>

      <Row label="QA check">
        {shownQa ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {shownQa.status === 'passed' ? <Pill tone="ok">Checked by QA</Pill> : <Pill tone="err">Failed QA</Pill>}
              <span className="text-xs text-content-subtle">{fmtUnix(shownQa.at)}{shownQa.by_name && ` · ${shownQa.by_name}`}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-[11px] font-medium rounded border px-1.5 ${shownQa.this_app ? 'border-border text-content-subtle' : 'border-primary/40 text-primary'}`}>
                {shownQa.this_app ? 'This bundle' : 'Other bundle'}
              </span>
              <span className="text-content break-all">{shownQa.bundle_id ?? shownQa.app_id}</span>
              <span className="font-mono text-xs text-content-subtle" title={`Build ${shownQa.build_id}`}>#{shortBuildId(shownQa.build_id)}</span>
            </div>
            {shownQa.note && (
              <div className={`text-xs text-content rounded-md border border-border border-l-2 px-2.5 py-1.5 ${shownQa.status === 'failed' ? 'border-l-danger' : 'border-l-success'}`}>
                <span className="block text-content-subtle mb-0.5">QA note</span>
                {shownQa.note}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="warn">Not checked</Pill>
            <span className="text-xs text-content-subtle">
              {qa ? 'No QA check since the new commits in this repo' : 'No QA check on any app from this repo yet'}
            </span>
          </div>
        )}
      </Row>

      <Row label="Repo">
        {!repo ? <span className="text-content-subtle">No base repository selected.</span> : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-content">{repo.owner}/{repo.repo}@{repo.branch || 'default'}</span>
              {repo.head && (
                <a href={repo.head.html_url} target="_blank" rel="noreferrer" className="font-mono text-xs text-primary hover:underline" title={repo.head.message}>
                  {short(repo.head.sha)}
                </a>
              )}
            </div>
            {repo.error ? (
              <div className="text-xs text-content-subtle">Could not read the repo: {repo.error}</div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-subtle">
                {repo.since && repo.ahead_by != null ? (
                  <span>
                    <b className={`font-semibold tabular-nums ${repo.ahead_by ? 'text-warning' : 'text-success'}`}>{repo.ahead_by}</b>
                    {' '}new commit{repo.ahead_by === 1 ? '' : 's'} {SINCE_LABEL[repo.since]}
                  </span>
                ) : apk && !repo.same_as_apk ? (
                  <span className="text-warning">The last APK was built from another repo or branch — nothing to compare.</span>
                ) : apk ? (
                  <span>Could not find the commit the APK was built from.</span>
                ) : null}
                {repo.head?.date && <span>last commit <b className="font-semibold text-content-muted">{formatDateTime(repo.head.date)}</b></span>}
                {!!repo.ahead_by && repo.compare_url && (
                  <a href={repo.compare_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">View diff ↗</a>
                )}
              </div>
            )}
          </>
        )}
      </Row>
    </div>
  );
}
